import { useEffect, useRef, useState } from 'react'
import Hls from 'hls.js'
import { Volume2, VolumeX, Maximize, Play, Pause, X, Airplay } from 'lucide-react'

interface VideoPlayerProps {
  src: string
  channelName: string
  channelIcon?: string
  onClose?: () => void
  index: number
}

export default function VideoPlayer({ src, channelName, channelIcon, onClose, index }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isPlaying, setIsPlaying] = useState(true)
  const [isMuted, setIsMuted] = useState(index > 0) // Mute all except first
  const [showControls, setShowControls] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [airplayAvailable, setAirplayAvailable] = useState(false)
  const hideControlsTimeout = useRef<number | null>(null)

  // Check for AirPlay availability (Safari/WebKit)
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    // Check if AirPlay is available (WebKit browsers)
    if ('webkitShowPlaybackTargetPicker' in video) {
      setAirplayAvailable(true)

      // Listen for AirPlay availability changes
      const handleAvailability = (event: Event) => {
        const e = event as CustomEvent
        setAirplayAvailable(e.detail?.available !== false)
      }

      video.addEventListener('webkitplaybacktargetavailabilitychanged', handleAvailability)
      return () => {
        video.removeEventListener('webkitplaybacktargetavailabilitychanged', handleAvailability)
      }
    }
  }, [])

  useEffect(() => {
    if (!videoRef.current || !src) return

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 30,
        maxBufferLength: 10,
        maxMaxBufferLength: 30,
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 6,
      })

      hls.loadSource(src)
      hls.attachMedia(videoRef.current)

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        videoRef.current?.play().catch(() => {})
      })

      hls.on(Hls.Events.ERROR, (_, data) => {
        console.error('[VideoPlayer] HLS Error:', {
          type: data.type,
          details: data.details,
          fatal: data.fatal,
          reason: data.reason,
          url: data.url,
        })

        if (data.fatal) {
          // Provide more specific error messages
          let errorMsg = `Stream error: ${data.type}`
          if (data.details === 'bufferAppendError' || data.details === 'bufferAppendingError') {
            errorMsg = 'Codec not supported (may be HEVC/H.265)'
          } else if (data.details === 'fragParsingError') {
            errorMsg = 'Stream format error'
          } else if (data.details) {
            errorMsg = `${data.type}: ${data.details}`
          }

          setError(errorMsg)

          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            // Try to recover
            hls.startLoad()
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError()
          }
        }
      })

      hlsRef.current = hls

      return () => {
        hls.destroy()
        hlsRef.current = null
      }
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS
      videoRef.current.src = src
      videoRef.current.play().catch(() => {})
    }
  }, [src])

  const togglePlay = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play()
        setIsPlaying(true)
      } else {
        videoRef.current.pause()
        setIsPlaying(false)
      }
    }
  }

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted
      setIsMuted(videoRef.current.muted)
    }
  }

  const toggleFullscreen = () => {
    if (containerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen()
      } else {
        containerRef.current.requestFullscreen()
      }
    }
  }

  const showAirplayPicker = () => {
    const video = videoRef.current as HTMLVideoElement & {
      webkitShowPlaybackTargetPicker?: () => void
    }
    if (video?.webkitShowPlaybackTargetPicker) {
      video.webkitShowPlaybackTargetPicker()
    }
  }

  const handleMouseMove = () => {
    setShowControls(true)
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current)
    }
    hideControlsTimeout.current = window.setTimeout(() => {
      setShowControls(false)
    }, 3000)
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full bg-black group"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setShowControls(false)}
    >
      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        muted={isMuted}
        playsInline
        autoPlay
        {...{ 'x-webkit-airplay': 'allow' } as any}
      />

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="text-center text-white">
            <p className="text-red-400 mb-2">{error}</p>
            <button
              onClick={() => {
                setError(null)
                hlsRef.current?.startLoad()
              }}
              className="px-4 py-2 bg-blue-600 rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Audio indicator - always visible when audio is active */}
      {!isMuted && (
        <div className="absolute top-3 right-3 p-1.5 rounded-full bg-black/30">
          <Volume2 className="w-4 h-4 text-gray-300/70" />
        </div>
      )}

      {/* Channel info overlay */}
      <div
        className={`absolute top-0 left-0 right-0 p-3 bg-gradient-to-b from-black/70 to-transparent transition-opacity ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="flex items-center gap-2">
          {channelIcon && (
            <img src={channelIcon} alt="" className="w-8 h-8 rounded object-cover" />
          )}
          <span className="text-white font-medium truncate">{channelName}</span>
        </div>
      </div>

      {/* Controls overlay */}
      <div
        className={`absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent transition-opacity ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={togglePlay}
              className="p-2 rounded-full hover:bg-white/20 text-white transition-colors"
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
            <button
              onClick={toggleMute}
              className="p-2 rounded-full hover:bg-white/20 text-white transition-colors"
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
          </div>
          <div className="flex items-center gap-2">
            {airplayAvailable && (
              <button
                onClick={showAirplayPicker}
                className="p-2 rounded-full hover:bg-white/20 text-white transition-colors"
                title="AirPlay"
              >
                <Airplay className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={toggleFullscreen}
              className="p-2 rounded-full hover:bg-white/20 text-white transition-colors"
            >
              <Maximize className="w-5 h-5" />
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-white/20 text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
